import { Button } from 'app/components/ui/button';
import LoadingSpinner from 'app/components/Common/LoadingSpinner';
import EventFieldSwitcher from 'app/components/CustomerForm/EventFieldSwitcher';
import { OveitIcon } from 'app/components/OveitIcon';
import axios from 'axios';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { EventFieldType } from 'types/EventField';
import { onIframeScrollIntoView, onResize } from 'utils/embed';
import { getEventIdFromSlug } from 'utils/events';
import { formatFieldsForAttendeesFormsRoute } from 'utils/registrationForms';
import { emailValidationSchema } from 'utils/schemaValidations';
import {
  API_REQUEST_STATUS_FAIL,
  API_REQUEST_STATUS_PENDING,
  API_REQUEST_STATUS_SUCCESS,
} from 'utils/static/constants';
import { FreeEventTicketType } from './useFreeEventData';

interface FreeEventRegistrationFormProps {
  requestStatus: string;
  setRequestStatus: (status: string) => void;
  selectedTicketTypesIsWhitelisted: boolean;
  registrationform: EventFieldType[] | null;
  ticketTypes: FreeEventTicketType[];
  selectedTicketType: number;
}

const FreeEventRegistrationForm: React.FC<FreeEventRegistrationFormProps> = ({
  registrationform,
  requestStatus,
  setRequestStatus,
  selectedTicketTypesIsWhitelisted,
  ticketTypes,
  selectedTicketType,
}) => {
  const { t, i18n } = useTranslation();
  const { eventSlug } = useParams();
  const [searchParams] = useSearchParams();
  const eventId = getEventIdFromSlug(eventSlug);
  const mandatoryEmailField = registrationform?.find(
    field => field.type === 'email' && field.mandatory,
  );
  const hasMandatoryEmail = !!mandatoryEmailField;

  const methods = useForm();
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = methods;

  const onSubmit = async data => {
    setRequestStatus(API_REQUEST_STATUS_PENDING);

    const fields = { ...data };

    if (!hasMandatoryEmail) {
      delete fields.email;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/events/${eventId}/process_free_order${
          searchParams.get('preview') ? '?preview=true' : ''
        }`,
        {
          email: hasMandatoryEmail
            ? data[`field-${mandatoryEmailField.id}`]
            : data.email,
          tickets: [
            {
              id: ticketTypes[selectedTicketType].id,
              quantity: 1,
            },
          ],
          fields: formatFieldsForAttendeesFormsRoute(fields),
          language: i18n.language,
        },
      );

      setRequestStatus(API_REQUEST_STATUS_SUCCESS);
    } catch (error) {
      setRequestStatus(API_REQUEST_STATUS_FAIL);
    } finally {
      const interval = setInterval(() => {
        onResize();
      }, 10);
      setTimeout(() => {
        onIframeScrollIntoView();
      }, 400);
      setTimeout(() => {
        clearInterval(interval);
      }, 800);
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit, onResize)}>
        <fieldset
          disabled={ticketTypes.length === 0}
          className="flex flex-col gap-[16px]"
        >
          {hasMandatoryEmail ? null : (
            <div>
              <label
                className="p3-semibold flex gap-[8px] items-center mandatory"
                htmlFor="email"
              >
                <OveitIcon name="email" />
                <span>{t('fields.email')}</span>
              </label>
              <input
                className={`standard-input w-full mt-[4px] ${
                  errors.email?.message && 'error'
                }`}
                type="text"
                {...register('email', {
                  required: {
                    value: true,
                    message: 'requiredField',
                  },
                  validate: {
                    isValid: async value => {
                      const emailIsValid = await emailValidationSchema.isValid(
                        value,
                      );
                      if (!emailIsValid) {
                        return 'validEmailAddress';
                      }
                    },
                  },
                })}
              />
              {errors.email?.message && (
                <p className="standard-input-error">
                  {t(`inputErrors.${errors.email.message}`)}
                </p>
              )}
            </div>
          )}
          {registrationform
            ? registrationform.map(field => (
                <EventFieldSwitcher
                  key={field.id}
                  inputName={`field-${field.id}`}
                  field={field}
                  disabled={false}
                />
              ))
            : null}
          <Button
            type="submit"
            size="lg"
            disabled={requestStatus === API_REQUEST_STATUS_PENDING}
          >
            {requestStatus === API_REQUEST_STATUS_PENDING ? (
              <LoadingSpinner variant="small" color="white" />
            ) : (
              t(
                `freeEventRegistrationPage.${
                  selectedTicketTypesIsWhitelisted ? 'apply' : 'register'
                }`,
              )
            )}
          </Button>
        </fieldset>
      </form>
    </FormProvider>
  );
};

export default FreeEventRegistrationForm;
